import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ALLOWED_FILE_SHARE_VCT_TYPE, MAX_UPLOAD_SIZE, } from 'src/app/conf/user-files';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import * as NotificationsActions from 'src/app/store/notifications/notifications.actions';
import { ImportFileSharingSuccess } from 'src/app/conf/devices-vct';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { MtmaGenericEntryInfoDialogComponent } from '../../mtma-generic-entry-info-dialog/mtma-generic-entry-info.dialog.component';
import { MtmaExportData, MtmaExportRow, MtmaUploadData } from 'src/app/model/mtma-import-export-file';
import { Observable, Subscription } from 'rxjs';
import * as DeviceMtmaActions from 'src/app/store/device-mtma/device-mtma.actions';
import { Device } from 'src/app/model/device';
import { selectCurrentDevice } from 'src/app/store/device/device.selector';
import { MtmaUploadErrorsMessageData, MtmaUploadResponseData, MtmaUploadWarningsMessageData } from 'src/app/model/api/data-rest';
import { selectMtmaUploadResponseData } from 'src/app/store/device-mtma/device-mtma.selector';

@Component({
	templateUrl: './mtma-import-file.dialog.component.html',
	providers: [ConfirmationService, DialogService]
})

export class MtmaImportFileDialogComponent implements OnInit, OnDestroy {
	rowCountStartRead: number = 4; // row from first (1) of file we need to skip for reading

	// Upload File
	fileToUpload: File;
	labelFile: string; // label file loaded
	showUploadedFileLabel: boolean;
	reuploadButton: boolean;

	text: string | ArrayBuffer; // text file readed
	processText: string; // log progress on textArea during uploading file

	maxUploadSize = MAX_UPLOAD_SIZE;

	ImportFileSharingSuccess = ImportFileSharingSuccess;
	importFileSuccess = ImportFileSharingSuccess.NOT_UPLOADED; // -1: not imported due to block errors, 0: not uploaded, 1: imported

	importListCsv: MtmaExportData[] = [];

	currentDevice: Device;
	currentDevice$: Observable<Device | undefined>;
	currentDeviceSub: Subscription;

	mtmaUploadResponseData: MtmaUploadResponseData;
	mtmaUploadResponseData$: Observable<MtmaUploadResponseData | undefined>;
	mtmaUploadResponseDataSub: Subscription;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private http: HttpClient,
		private translate: TranslateService,
		public confirmationService: ConfirmationService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.store.dispatch(DeviceMtmaActions.resetMtmaImportCsv());
		this.subscribeAll();
		this.onResize();
	}

	initSelectors(): void {
		this.uploadInit();
		this.currentDevice$ = this.store.select(selectCurrentDevice);
		this.mtmaUploadResponseData$ = this.store.select(selectMtmaUploadResponseData);
	}

	subscribeAll(): void {
		this.currentDeviceSub = this.currentDevice$?.subscribe(currentDevice => {
			this.currentDevice = currentDevice;
		});
		this.mtmaUploadResponseDataSub = this.mtmaUploadResponseData$?.subscribe(mtmaUploadResponseData => {
			this.mtmaUploadResponseData = mtmaUploadResponseData;
			if (this.mtmaUploadResponseData) {
				this.elaborateResponseUploadApi();
			}
		});
	}

	ngOnDestroy(): void {
		this.currentDeviceSub?.unsubscribe();
		this.mtmaUploadResponseDataSub?.unsubscribe();
	}

	closeDialog(): void {
		this.ref.close(this.importFileSuccess);
	}

	handleFileInput(event) {
		if (event.currentFiles.length > 0) {
			this.fileToUpload = event.currentFiles[0];
			if (!ALLOWED_FILE_SHARE_VCT_TYPE.includes(this.fileToUpload.type)) {
				const notification = new Notification("FILE_NOT_COMPATIBLE", this.translate.instant("MAX_UPLOAD_SIZE_1") + " 900KB", NotificationLevel.Error, '');
				this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));

				this.setReupload();
			} else if (this.fileToUpload.size > MAX_UPLOAD_SIZE) {
				const notification = new Notification("FILE_TOO_BIG", this.translate.instant("MAX_UPLOAD_SIZE_1") + " 900KB", NotificationLevel.Error, '');
				this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));

				this.setReupload();
			} else {
				const myReader: FileReader = new FileReader();
				myReader.onloadend = () => {
					this.processText = this.processText + this.translate.instant('VCT_UPLOAD_FILE_UPLOAD') + '....\n';
					this.text = myReader.result;
					this.elaborateFile(this.text.toString());
				};
				myReader.readAsText(this.fileToUpload);
				this.labelFile = this.fileToUpload.name;
				this.completedUploadSuccess();
			}
		}
	}

	elaborateFile(file: string): void {
		this.processText = this.processText + this.translate.instant('VCT_UPLOAD_READING_PROGRESS') + '....\n';

		// Splitting row file
		const validRowsFile : string[] = file
			.split(/[\r\n]+/)             // Split rows
			.filter(line => line.trim()); // Remove empty rows or with only spaces

		const headerLineNumber: number = this.rowCountStartRead - 1;
		const headerLine: string = validRowsFile[headerLineNumber - 1]; // index: [headerLineNumber-1] because array starts from 0
		const columnsLine = headerLine.split(',');
		const columnNumber = 14;
		// check if file have right number of column header
		if (this.errorLineColumnCount(columnsLine,columnNumber)) {
			this.processText = this.processText + this.translate.instant('VCT_UPLOAD_ERRORE_ROW') + ': ' + headerLineNumber + ' \n';
			this.processText = this.processText + this.translate.instant('INVALID_NUMBER_COLUMNS_FILE') + ' \n';
			this.processText = this.processText + this.translate.instant('VCT_UPLOAD_READING_DONE_FAILED') + '\n';
			const notification = new Notification("ERROR_82", this.translate.instant("INVALID_NUMBER_COLUMNS_FILE"), NotificationLevel.Error, '');
			this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));

			this.setReupload();
			this.importFileSuccess = ImportFileSharingSuccess.NOT_IMPORTED_BLOCK_ERRORS;
			setTimeout(() => {
				this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'import_mtm_config_file_failed_witherrors_column_struct', params: { page_title: 'MTMA | Import/Export' } }));
			}, 2000);
			return;
		}
		// check if file have right format of column header
		if (this.errorLineColumnFormat(columnsLine)) {
			this.processText = this.processText + this.translate.instant('VCT_UPLOAD_ERRORE_ROW') + ': ' + headerLineNumber + ' \n';
			this.processText = this.processText + this.translate.instant('FORMAT_NUMBER_COLUMNS_FILE') + ' \n';
			this.processText = this.processText + this.translate.instant('VCT_UPLOAD_READING_DONE_FAILED') + '\n';
			const notification = new Notification("ERROR_82", this.translate.instant("FORMAT_NUMBER_COLUMNS_FILE"), NotificationLevel.Error, '');
			this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));

			this.setReupload();
			this.importFileSuccess = ImportFileSharingSuccess.NOT_IMPORTED_BLOCK_ERRORS;
			setTimeout(() => {
				this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'import_mtm_config_file_failed_witherrors_format_struct', params: { page_title: 'MTMA | Import/Export' } }));
			}, 2000);
			return;
		}

		this.processText = this.processText + this.translate.instant('VCT_UPLOAD_READING_DONE') + '\n';
		this.processText = this.processText + this.translate.instant('VCT_UPLOAD_FILE_PROCESSING_PROGRESS') + '....\n';

		// check if each row have right datas
		// if we have any errors we skip read and load row and we continue until the end of file
		// is better check each row due to possibile unified cells
		let lineCont = 0;
		for (const line of validRowsFile) {
			lineCont++;
			if (lineCont >= this.rowCountStartRead) {
				if (!this.elaborateLine(line)) {
					this.processText = this.processText + this.translate.instant('VCT_UPLOAD_ERRORE_ROW') + ': ' + lineCont + ' --- ' + this.translate.instant('ROW_NOT_LOADED') + ' \n';
				}
			}
		}

		this.processText = this.processText + this.translate.instant('VCT_UPLOAD_FILE_PROCESSING_DONE') + '\n';
		this.writeStruct();
	}
	
	// --- START: ELABORATELINE AUX FUNCTS -----------------
	errorLineColumnCount(columnsLine: string[], columnNumber: number): boolean {
		// check just if there is last column
		if (columnsLine.length > columnNumber || columnsLine.length < columnNumber) {
			return true;
		}

		// check if each cellContent is not empty cell
		const hasEmptyCell = columnsLine.some(cellContent => cellContent === '');
		if (hasEmptyCell) {
			return true;
		}
		return false;
	}

	errorLineColumnFormat(columnsLine: string[]): boolean {
		// check if each cellContent have right string format
		if (columnsLine[0] !== 'Type' || columnsLine[1] !== 'Name' || columnsLine[2] !== 'Description' || columnsLine[3] !== 'Phone' ||
			columnsLine[4] !== 'UnitCode' || columnsLine[5] !== 'KeypadTagCode' || columnsLine[6] !== 'Action1' || columnsLine[7] !== 'Action2' ||
			columnsLine[8] !== 'Group' || columnsLine[9] !== 'Contact1' || columnsLine[10] !== 'Contact2' || columnsLine[11] !== 'Contact3' ||
			columnsLine[12] !== 'Contact4' || columnsLine[13] !== 'Contact5'
		) {
			return true;
		}
		return false;
	}

	// elaborate each row data 
	// return true: everything was success
	// return false: error row
	elaborateLine(line: string): boolean {
		const columnFile : string[] = line.split(',');
		const columnType : string = columnFile[0];
		switch (columnType) {
			case 'MTMA_CONTACTS': {
				const mandatoryContactsFields: string[] = ['Name','Description','Phone','Action1','Action2','Group'];
				return this.extractDataRow(columnFile, mandatoryContactsFields);
			}
			case 'MTMA_UNITS': {
				const mandatoryUnitsFields: string[] = ['Name','Description','UnitCode','Contact1','Contact2','Contact3', 'Contact4', 'Contact5'];
				return this.extractDataRow(columnFile, mandatoryUnitsFields);
			}
			case 'MTMA_KEYPADS': {
				const mandatoryKeypadsFields: string[] = ['Description','KeypadTagCode','Action1','Action2','Group'];
				return this.extractDataRow(columnFile, mandatoryKeypadsFields);
			}
			case 'MTMA_TAGS':{
				const mandatoryTagsFields: string[] = ['Description','KeypadTagCode','Action1','Action2','Group'];
				return this.extractDataRow(columnFile, mandatoryTagsFields);
			}
			default:
				return false;
		}
	}

	// for each valid row we build a base struct
	// MtmaExportRow is a struct as csv file
	// we'll cast and convert a MtmaExportRow array into a MtmaExportData array for POST api in a final step
	createLineMtmaExportRow(columnFile: string[]): MtmaExportRow {
		const rowData : MtmaExportRow = {
			Type: columnFile[0],
			Name: columnFile[1],
			Description: columnFile[2],
			Phone: columnFile[3],
			UnitCode: columnFile[4],
			KeypadTagCode: columnFile[5],
			Action1: columnFile[6],
			Action2: columnFile[7],
			Group: columnFile[8],
			Contact1: columnFile[9],
			Contact2: columnFile[10],
			Contact3: columnFile[11],
			Contact4: columnFile[12],
			Contact5: columnFile[13]
		};
		return rowData;
	}

	// we check each cell has a right format data csv write handle
	checkDataRow(rowData : MtmaExportRow, mandatoryFields: string[]): boolean {
		for (const key of Object.keys(rowData)) {
			// Type is excludes because is just ok
			if (key === 'Type') continue;

			const value = rowData[key];
			if (mandatoryFields.includes(key)) {
				// mandatory field has not right format -> exit with false else continue
				if (!value.startsWith('##') || !value.endsWith('**')) {
					return false;
				}
			} else {
				// not mandatory field is not empty string -> exit with false else continue
				if (value !== '') {
					return false;
				}
			}
		}
		// every format field is ok
		return true;
	}

	buildMtmaExportDataRow(rowData: MtmaExportRow, mandatoryFields: string[]): MtmaExportData {
		// we extract clean value of mandatoryFields cells and set as null others
		// Type is excludes because is just ok
		Object.keys(rowData).forEach(key => {
			if (key !== 'Type') {
				if (mandatoryFields.includes(key)) {
					rowData[key] = rowData[key].replace('##', '').replace('**', '');
				} else {
					rowData[key] = null;
				}
			}
		});

		const mtmaExportData = new MtmaExportData();
		mtmaExportData.Type = rowData.Type;
		mtmaExportData.Name = rowData.Name;
		mtmaExportData.Description = rowData.Description;
		mtmaExportData.Phone = rowData.Phone;
		mtmaExportData.KeyboardCode = rowData.UnitCode;
		mtmaExportData.Code = rowData.KeypadTagCode;
		mtmaExportData.Action1 = rowData.Action1;
		mtmaExportData.Action2 = rowData.Action2;
		mtmaExportData.RuleName = rowData.Group;
		mtmaExportData.Contact1Name = rowData.Contact1;
		mtmaExportData.Contact2Name = rowData.Contact2;
		mtmaExportData.Contact3Name = rowData.Contact3;
		mtmaExportData.Contact4Name = rowData.Contact4;
		mtmaExportData.Contact5Name = rowData.Contact5;

		return mtmaExportData;
	}

	// extract and insert in a MtmaExportData array a valid row
	extractDataRow(columnFile: string[], mandatoryFields: string[]): boolean {
		const rowData : MtmaExportRow = this.createLineMtmaExportRow(columnFile);

		if (this.checkDataRow(rowData, mandatoryFields)) {
			this.importListCsv.push(this.buildMtmaExportDataRow(rowData, mandatoryFields));
			return true; // success, inserted into MtmaExportData array
		} else {
			return false; // error row, not inserted into MtmaExportData array
		}
	}

	// we have elaborated file so we call POST api
	writeStruct(): void {
		this.processText = this.processText + this.translate.instant('VCT_UPLOAD_WRITE_DATA_PROGRESS') + '....\n';

		const mtmaUploadData : MtmaUploadData = {
			Data: this.importListCsv,
			ImportContacts: true,
			ImportUnits: true,
			ImportKeypads: true,
			ImportTags: true
		}
		this.store.dispatch(DeviceMtmaActions.postMtmaImportCsv({ deviceId: this.currentDevice.Id, mtmaUploadData: mtmaUploadData }));
	}
	// --- END: ELABORATELINE AUX FUNCTS -----------------

	// we sent /upload POST api so we elaborate response
	elaborateResponseUploadApi(): void {
		if (this.mtmaUploadResponseData) {
			if (this.mtmaUploadResponseData.Success) {
				const mtmaErrors : MtmaUploadErrorsMessageData[] = this.mtmaUploadResponseData.Errors;
				const mtmaWarnings: MtmaUploadWarningsMessageData[] = this.mtmaUploadResponseData.Warnings;
				if (mtmaErrors && mtmaErrors.length > 0 || mtmaWarnings && mtmaWarnings.length > 0) {
					// print log errors message from API response
					if (mtmaErrors && mtmaErrors.length > 0) {
						mtmaErrors.forEach(mtmaError => {
							this.processText = this.processText + this.translate.instant('MTMA_CONF_{Error}') + ': ' + this.translate.instant(mtmaError.Message) + '\n';
						});
					}
					// print log warnings message from API response
					if (mtmaWarnings && mtmaWarnings.length > 0) {
						mtmaWarnings.forEach(mtmaWarning => {
							this.processText = this.processText + this.translate.instant('WARNING_ANOMALY') + ': ' + this.translate.instant(mtmaWarning.Name) + ' --- ' + this.translate.instant(mtmaWarning.Message) + '\n';
						});
					}
					// Analytics Events and response tracking for parent component
					if (mtmaErrors && mtmaErrors.length > 0 && mtmaWarnings && mtmaWarnings.length > 0) {
						this.importFileSuccess = ImportFileSharingSuccess.IMPORTED_SUCCESS_WARNINGS_AND_ERRORS;

						const notification = new Notification("DEVICE-MTMA", this.translate.instant('VCT_UPLOAD_WRITE_DATA_DONE_WITH_ERRORS_AND_WARNINGS'), NotificationLevel.Warning, '');
						this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
						setTimeout(() => {
							this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'import_mtm_file_success_witherrorsandwarnings', params: { page_title: 'MTMA | Import/Export' } }));
						}, 2000);
					} else {
						if (mtmaWarnings && mtmaWarnings.length > 0) {
							this.importFileSuccess = ImportFileSharingSuccess.IMPORTED_SUCCESS_WARNINGS;

							const notification = new Notification("DEVICE-MTMA", this.translate.instant('VCT_UPLOAD_WRITE_DATA_DONE_WITH_WARNING'), NotificationLevel.Warning, '');
							this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
							setTimeout(() => {
								this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'import_mtm_file_success_withwarnings', params: { page_title: 'MTMA | Import/Export' } }));
							}, 2000);
						}
						if (mtmaErrors && mtmaErrors.length > 0) {
							this.importFileSuccess = ImportFileSharingSuccess.IMPORTED_SUCCESS_ERRORS;

							const notification = new Notification("DEVICE-MTMA", this.translate.instant('VCT_UPLOAD_WRITE_DATA_DONE_WITH_ERRORS'), NotificationLevel.Warning, '');
							this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
							setTimeout(() => {
								this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'import_mtm_file_success_witherrors', params: { page_title: 'MTMA | Import/Export' } }));
							}, 2000);
						}
					}

					this.processText = this.processText + this.translate.instant('VCT_UPLOAD_WRITE_DATA_DONE') + '\n';
				} else {
					// success without warnings and errors
					const notification = new Notification("DEVICE-MTMA", this.translate.instant('VCT_UPLOAD_WRITE_DATA_DONE'), NotificationLevel.Success, '');
					this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));

					this.processText = this.processText + this.translate.instant('VCT_UPLOAD_WRITE_DATA_DONE') + '\n';
					this.importFileSuccess = ImportFileSharingSuccess.IMPORTED_SUCCESS; // import success

					setTimeout(() => {
						this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'import_mtm_file_success_noerrors', params: { page_title: 'MTMA | Import/Export' } }));
					}, 2000);
				}
			} else {
				// failed upload import
				// we dont have dispatch notification because we catch fail response in notifyErrors device-mtma.effects.ts store file
				this.processText = this.processText + this.translate.instant('VCT_UPLOAD_WRITE_DATA_FAILED') + '\n';
				this.importFileSuccess = ImportFileSharingSuccess.NOT_IMPORTED_BLOCK_ERRORS; // not imported due to blocked error

				setTimeout(() => {
					this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'import_mtm_file_failed_witherrors', params: { page_title: 'MTMA | Import/Export' } }));
				}, 2000);
			}
		}
	}

	isEmailValid(email: string): boolean {
		if (email === '') {
			return true;
		}
		const re = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/;
		return re.test(email.toLowerCase());
	}

	uploadInit() {
		this.labelFile = '';
		this.processText = '';
		this.showUploadedFileLabel = false;
		this.reuploadButton = false;
		this.fileToUpload = null;
	}

	setReupload() {
		this.showUploadedFileLabel = false;
		this.reuploadButton = true;
	}

	completedUploadSuccess() {
		this.showUploadedFileLabel = true;
		this.reuploadButton = false;
	}

	infoDialogGenericEntry() {
		// valid only for SUB_ACTION
		this.dialogService.open(MtmaGenericEntryInfoDialogComponent, {
			header: this.translate.instant('MTMA_CONF_{Information}') + ': ' + this.translate.instant('IMPORT_DATAS_FROM_FILE'),
			contentStyle: { overflow: 'auto' },
			data: {
				row1: 'INFO_IMPORT_DEVICE',
				row2: 'IMPORT_EXPORT_MTMA_WARNING',
				icon: 'pi cm-alert'
			}
		});
	}

	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile() {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-20rem');
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}
}